import { useState, useRef, useEffect } from "react";

interface CityItem {
  code: string;
  city: string;
  province: string;
  latlon?: string;
  level: number;
  aleph: string;
  pinyin?: string;
}

interface ProvinceData {
  province: { code: string; province: string; latlon: string; aleph: string };
  citys: CityItem[];
}

interface CityGroup {
  aleph: string;
  citys: CityItem[];
}

const API_BASE = 'https://www.daditu.com.cn/bigmap/dzm';

interface CityPickerProps {
  city: string;
  onSelect: (city: string) => void;
  onLocationSelect?: (lon: number, lat: number, name: string, data?: Record<string, any>) => void;
  onGridSelect?: (data: { type: string; data: any }) => void;
  mapSelectedStreet?: { gid: string; name: string } | null;
  inline?: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
}

export default function CityPicker({ city, onSelect, onLocationSelect, onGridSelect, mapSelectedStreet, inline = false, containerRef }: CityPickerProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"letter" | "province">("letter");
  const [searchInput, setSearchInput] = useState("");
  const [activeLetter, setActiveLetter] = useState("A");
  const [defaultCity, setDefaultCity] = useState("南京");
  const [locating, setLocating] = useState(false);
  const [activeProvince, setActiveProvince] = useState("");

  // 真实 API 数据
  const [citiesByLetter, setCitiesByLetter] = useState<Record<string, CityItem[]>>({});
  const [provincesList, setProvincesList] = useState<ProvinceData[]>([]);
  const [loading, setLoading] = useState(false);
  const [alphabet, setAlphabet] = useState<string[]>([]);

  const listRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 获取真实省市数据
  useEffect(() => {
    if (!open) return;
    if (provincesList.length > 0 && Object.keys(citiesByLetter).length > 0) return; // 已加载

    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/getProvinceAndCities`).then(r => r.json()),
      fetch(`${API_BASE}/getByCities`).then(r => r.json()),
    ]).then(([provinceRes, letterRes]) => {
      // 按省份分组
      if (provinceRes?.data) {
        setProvincesList(provinceRes.data);
        if (provinceRes.data.length > 0) {
          setActiveProvince(provinceRes.data[0].province.province);
        }
      }

      // 按字母分组
      if (letterRes?.data) {
        const grouped: Record<string, CityItem[]> = {};
        const letters: string[] = [];
        letterRes.data.forEach((g: CityGroup) => {
          grouped[g.aleph] = g.citys;
          letters.push(g.aleph);
        });
        setCitiesByLetter(grouped);
        setAlphabet(letters);
      }
    }).catch(err => {
      console.error('获取省市数据失败:', err);
    }).finally(() => {
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-detect city on mount
  useEffect(() => {
    handleLocate(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 面板仅通过点击地点按钮切换开闭

  const handleLocate = (silent = false) => {
    if (!silent) setLocating(true);
    setTimeout(() => {
      const detected = "南京";
      onSelect(detected);
      if (!silent) setLocating(false);
    }, 800);
  };

  const handleSelect = (c: string, latlon?: string, level?: number) => {
    // API 返回的城市名带"市"后缀，去掉后缀
    const cityName = c.replace(/市$/, '');
    onSelect(cityName);
    // 直接用接口里的坐标飞行定位，并传入级别控制相机高度
    let flew = false;
    if (latlon && onLocationSelect) {
      const [lng, lat] = latlon.split(',').map(Number);
      if (!isNaN(lng) && !isNaN(lat)) {
        onLocationSelect(lng, lat, cityName, { level });
        flew = true;
      }
    }
    // 标记飞行状态，通知 MapPage 的 city useEffect 跳过备用飞行
    (window as any).__cityFlyDone = flew;
  };

  const handleSetDefault = () => {
    setDefaultCity(city);
  };

  const scrollToLetter = (letter: string) => {
    setActiveLetter(letter);
    const el = listRef.current?.querySelector(`[data-letter="${letter}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // 从 API 数据中查找城市的坐标和级别
  const findCityData = (name: string): { latlon: string; level: number } | null => {
    const searchName = name.endsWith('市') ? name : name + '市';
    // 先从字母分组查找
    for (const cities of Object.values(citiesByLetter)) {
      const found = cities.find(c => c.city === searchName);
      if (found?.latlon) return { latlon: found.latlon, level: found.level };
    }
    // 再从省份分组查找
    for (const p of provincesList) {
      const found = p.citys.find(c => c.city === searchName);
      if (found?.latlon) return { latlon: found.latlon, level: found.level };
    }
    return null;
  };

  const allCityNames = Object.values(citiesByLetter).flat().map(i => i.city);
  const filteredCities = searchInput.trim()
    ? allCityNames.filter(c => c.includes(searchInput.trim()))
    : [];

  // 获取当前省份的城市列表
  const currentProvinceCities = provincesList.find(
    p => p.province.province === activeProvince
  )?.citys || [];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 text-sm text-slate-800 hover:text-slate-900 transition-colors cursor-pointer whitespace-nowrap ${
          inline
            ? 'px-2.5 py-2 bg-transparent rounded-l-lg hover:bg-slate-50'
            : 'bg-white border border-slate-200 rounded-lg px-3 py-2.5 hover:border-blue-400'
        }`}
      >
        <i className="ri-map-pin-line text-blue-500 text-sm"></i>
        <span className="font-medium">{city}</span>
        <i className={`ri-arrow-down-s-line text-slate-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}></i>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute top-11 left-0 bg-white/95 backdrop-blur-md border border-slate-200/80 rounded-xl z-50 overflow-hidden"
          style={{
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            width: 540,
          }}
        >
          {/* Header: current city & default */}
          <div className="border-b border-slate-100 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-600">当前城市：
                <span className="text-blue-600 font-semibold ml-1">{city}</span>
              </span>
              <span className="text-slate-300">|</span>
              <span className="text-slate-600">默认城市：
                <span className="text-slate-800 font-semibold ml-1">{defaultCity}</span>
                <button
                  onClick={handleSetDefault}
                  className="ml-1.5 text-xs text-blue-500 hover:text-blue-700 cursor-pointer transition-colors whitespace-nowrap"
                >[设为默认]</button>
              </span>
            </div>
            <button
              onClick={() => handleLocate(false)}
              className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 cursor-pointer transition-colors whitespace-nowrap"
            >
              {locating
                ? <><i className="ri-loader-4-line animate-spin"></i> 定位中...</>
                : <><i className="ri-map-pin-2-line"></i> 重新定位</>
              }
            </button>
          </div>

          {/* 常用城市 */}
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="text-xs text-slate-600 font-semibold mb-2">常用城市</div>
            <div className="flex items-center gap-1.5 flex-nowrap">
              {["北京","南京","上海","广州","深圳","成都","天津","杭州"].map(c => (
                <button
                  key={c}
                  onClick={() => {
                    const data = findCityData(c);
                    handleSelect(c, data?.latlon, data?.level);
                  }}
                  className={`px-3 py-1 rounded text-xs cursor-pointer transition-colors whitespace-nowrap ${
                    city === c
                      ? "bg-blue-500 text-white font-medium"
                      : "bg-white border border-slate-300 text-slate-800 hover:border-blue-400 hover:text-blue-600"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Tab + search */}
          <div className="px-4 pt-3 pb-0 flex items-center gap-0 border-b border-slate-100">
            <div className="flex">
              <button
                onClick={() => setTab("letter")}
                className={`px-4 py-2 text-sm cursor-pointer transition-colors whitespace-nowrap border-b-2 ${
                  tab === "letter" ? "text-blue-600 border-blue-500 font-semibold" : "text-slate-500 border-transparent hover:text-slate-800"
                }`}
              >
                按城市
              </button>
              <button
                onClick={() => setTab("province")}
                className={`px-4 py-2 text-sm cursor-pointer transition-colors whitespace-nowrap border-b-2 ${
                  tab === "province" ? "text-blue-600 border-blue-500 font-semibold" : "text-slate-500 border-transparent hover:text-slate-800"
                }`}
              >
                按省份
              </button>
            </div>
            <div className="ml-auto mb-2 flex items-center border border-slate-200 rounded overflow-hidden bg-white">
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="输入城市名"
                className="text-xs text-slate-800 placeholder-slate-400 px-2.5 py-1.5 outline-none w-28"
              />
              <button className="px-2 py-1.5 text-slate-400 hover:text-blue-500 cursor-pointer">
                <i className="ri-search-line text-sm"></i>
              </button>
            </div>
          </div>

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin"></div>
              <span className="ml-2 text-sm text-slate-500">加载中...</span>
            </div>
          )}

          {/* Search results */}
          {!loading && searchInput.trim() && (
            <div className="px-4 py-3 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto border-b border-slate-100">
              {filteredCities.length > 0 ? filteredCities.map(c => (
                <button
                  key={c}
                  onClick={() => handleSelect(c)}
                  className="px-3 py-1 rounded text-xs bg-white border border-slate-300 text-slate-800 hover:border-blue-400 hover:text-blue-600 cursor-pointer transition-colors whitespace-nowrap"
                >
                  {c}
                </button>
              )) : (
                <span className="text-xs text-slate-600">未找到匹配城市</span>
              )}
            </div>
          )}

          {/* City list by letter */}
          {!loading && !searchInput.trim() && tab === "letter" && (
            <div className="flex overflow-x-hidden" style={{ height: "260px" }}>
              {/* Alphabet sidebar */}
              <div className="flex flex-col items-center py-2 pl-5 pr-2 border-r border-slate-100 overflow-y-auto overflow-x-hidden w-16 flex-shrink-0">
                {alphabet.map(l => (
                  <button
                    key={l}
                    onClick={() => scrollToLetter(l)}
                    className={`w-6 h-6 flex items-center justify-center text-xs rounded cursor-pointer transition-colors whitespace-nowrap mb-0.5 ${
                      activeLetter === l ? "bg-blue-500 text-white font-semibold" : "text-slate-600 hover:text-blue-600 hover:bg-blue-50"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              {/* City groups */}
              <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-2">
                {alphabet.map(letter => {
                  const cities = citiesByLetter[letter] || [];
                  if (cities.length === 0) return null;
                  return (
                    <div key={letter} data-letter={letter} className="mb-3">
                      <div className="text-xs font-bold text-slate-700 mb-1.5 sticky top-0 bg-white pb-0.5 border-b border-slate-200">{letter}</div>
                      <div className="flex flex-wrap gap-1 pt-1">
                        {cities.map(item => (
                          <button
                            key={item.code}
                            onClick={() => handleSelect(item.city, item.latlon, item.level)}
                            className={`px-2.5 py-0.5 rounded text-xs cursor-pointer transition-colors whitespace-nowrap ${
                              city === item.city
                                ? "bg-blue-500 text-white font-medium"
                                : "bg-white border border-slate-300 text-slate-800 hover:border-blue-400 hover:text-blue-600"
                            }`}
                          >
                            {item.city}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* City list by province */}
          {!loading && !searchInput.trim() && tab === "province" && (
            <div className="flex overflow-x-hidden" style={{ height: "260px" }}>
              {/* Province list */}
              <div className="overflow-y-auto overflow-x-hidden border-r border-slate-100 py-1 pl-3 pr-1.5 w-24 flex-shrink-0">
                {provincesList.map(p => (
                  <button
                    key={p.province.code}
                    onClick={() => setActiveProvince(p.province.province)}
                    className={`w-full text-left px-2 py-2 text-xs cursor-pointer transition-colors ${
                      activeProvince === p.province.province
                        ? "text-blue-600 bg-white font-semibold border-r-2 border-blue-500"
                        : "text-slate-700 hover:bg-white hover:text-slate-900"
                    }`}
                  >
                    <span className="block truncate">{p.province.province}</span>
                  </button>
                ))}
              </div>
              {/* Cities of selected province */}
              <div className="flex-1 overflow-y-auto px-3 py-3">
                <div className="text-xs text-slate-600 font-semibold mb-2">{activeProvince} · 所有城市</div>
                <div className="flex flex-wrap gap-1.5">
                  {currentProvinceCities.map(item => (
                    <button
                      key={item.code}
                      onClick={() => handleSelect(item.city, item.latlon, item.level)}
                      className={`px-3 py-1 rounded text-xs cursor-pointer transition-colors whitespace-nowrap ${
                        city === item.city
                          ? "bg-blue-500 text-white font-medium"
                          : "bg-white border border-slate-300 text-slate-800 hover:border-blue-400 hover:text-blue-600"
                      }`}
                    >
                      {item.city}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
